import React, {useState, useEffect} from "react";
import { UpdateFieldFn, Golfer } from "../types/types";
import { useNavigate, useLocation } from "react-router-dom";
import { getProducts } from "../api/api-hook";
import Footer from "./shared/Footer";
import { normalizeInput, formatDOB, convertFromISOToUS, isValidDOB } from "../utils/utils";
import { usePageContent } from "../api/usePageContent";
import { appAsset } from "../utils/appAsset";

interface Step3Props {
  formData: Golfer;
  updateField: UpdateFieldFn;
}

interface VipProduct {
  id: string;
  name: string;
  label: string;
  description?: string;
  price?: number;
  product_category:string;
  tabKey: string;
  imageUrl: string;
  under_25:string;
  product_id: string;
  bagTagPrice:number;
}

interface LocationState {
  golfer?: Golfer;
}

const Step5: React.FC<Step3Props> = ({ formData, updateField }) => {
  const { title: pageTitle, overview: pageOverview } = usePageContent("step5");
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [activeTabKey, setActiveTabKey] = useState<string>("vip"); // default tab
  const [loading, setLoading] = useState(false);
  const isAdmin = location.state?.admin; 
  const [products, setProducts] = useState<VipProduct[]>([]);
  const state = (location.state || {}) as LocationState;
  
    const [golfer, setGolfer] = useState<Golfer | null>(
      state.golfer ?? null
    );
  
 useEffect(() => {
      if (state.golfer) {
        updateField("id", state.golfer?.id as any || "");
        updateField("firstName", state.golfer?.firstName || "");
        updateField("lastName", state.golfer?.lastName || "");
        updateField("email", state.golfer?.email || "");
        updateField("phone", normalizeInput(state.golfer?.phone));
        updateField("sex", state.golfer?.sex || "");
        if (state.golfer.id) {
          updateField("id", state.golfer.id as any || '');
        }
        if (state.golfer?.date_of_birth && state.golfer?.date_of_birth.includes("-")) {
          updateField("date_of_birth", convertFromISOToUS(state.golfer?.date_of_birth));
        } else {
          updateField("date_of_birth", (state.golfer?.date_of_birth || ""));
        }
        updateField("address", state.golfer?.address || "");
        updateField("street_address_2", state.golfer?.street_address_2 || "");
        updateField("city", state.golfer?.city || "");
        updateField("state", state.golfer?.state || "");
        updateField("zip", state.golfer?.zip || "");
 
        updateField("bagTag", !!state.golfer?.bagTag);
       updateField("donation", !!state.golfer?.donation);
       updateField("personalization", state.golfer?.personalization || '');
       updateField("donation_amount", state.golfer?.donation_amount || 0);
       
        updateField("vip_card_type", state.golfer?.vip_card_type || "");
        updateField("vip_package", state.golfer?.vip_package || "");
        updateField("ghinNumber", state.golfer?.ghinNumber || "");
        updateField("product_id", state.golfer?.product_id || "");
        updateField("bagTagPrice", state.golfer?.bagTagPrice || 0);
      }
    }, []); 
 

  
  const handleFieldChange = (field: keyof Golfer) => 
          (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            const target = e.target;

            let value: any = target.value;


            if (target instanceof HTMLInputElement && target.type === "checkbox") {
              value = target.checked;
            }
        
            // 1. update your global form data
            updateField(field, value);

             // 2. extra side-effects for related fields
            if (field === "bagTag" && !value) {
              // bagTag turned OFF → clear personalization
              updateField("personalization", "");
            }

            if (field === "donation" && !value) {
              // donation turned OFF → clear donation amount
              updateField("donation_amount", 0);
            }
      
            // 2. also keep local golfer in sync
            setGolfer((prev) => {
              const base = prev ?? {};
        
              // map FormData field name ⇒ Golfer field name
              if (field === "bagTag") {
                return { ...base, bagTag: Boolean(value), personalization: value ? base.personalization ?? "" : "" };
              }
              if (field === "donation") {
                return { ...base, donation: Boolean(value), donation_amount: value ? (base.donation_amount ?? 0) : 0 };
              }
              if (field === "personalization") {
                return { ...base, personalization: value};
              }
              if (field === "donation_amount") {
                return { ...base, donation_amount: value };
              }
              if (field === "bagTagPrice") {
                return { ...base, bagTagPrice: value };
              }
   
              return base;
            });
          };
      



useEffect(() => {
    // Clear router state if any
    async function loadProducts() {
      try {
        setLoading(true);
        setError(null);
        const raw = await getProducts({
          property: "product_category",
          value: "addon"
        });

        const data: VipProduct[] =
          typeof raw === "string" ? JSON.parse(raw) : raw;

          
          setProducts(data);

          const bagTagProduct = data.find(p =>
            (p.tabKey || "").toLowerCase().includes("bagtag") ||
            (p.name || "").toLowerCase().includes("bagtag") ||
            (p.label || "").toLowerCase().includes("bagtag")
          );
          
          if (bagTagProduct?.price != null) {
            updateField("bagTagPrice" as any, Number(bagTagProduct.price));
          }
          


          if (data.length > 0) {
            setActiveTabKey(data[0].tabKey); // first in sorted order
          }

  
      } catch (err) {
        console.error(err);
        setError("Failed to load products");
      } finally {
        setLoading(false);
      }
    }
  
    loadProducts();  // call the async function

  }, []);


  const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
    
      // build an updated golfer from the latest formData if you want to be explicit:
      const updatedGolfer: Golfer | null = golfer
        ? {
            ...golfer,
            firstName: formData.firstName || "",
            lastName: formData.lastName || "",
            email: formData.email || "",
            phone: formData.phone || "",
            sex: formData.sex || "",
            date_of_birth: formData.date_of_birth || "",
            address: formData.address || "",
            street_address_2: formData.street_address_2 || "",
            city: formData.city || "",
            state: formData.state || "",
            zip: formData.zip || "",
            id: formData.id as any || "",
            bagTag: formData.bagTag || false,
            donation: formData.donation || false,
            personalization: formData.personalization || "",
            donation_amount: formData.donation_amount || 0,
            vip_package: formData.vip_package || "",
            vip_card_type: formData.vip_card_type || "",
             ghinNumber: formData.ghinNumber || "",
             product_id: formData.product_id || "",
             bagTagPrice: formData.bagTagPrice || 0
          }
        : null;
    
      navigate("/step6", {
        state: {
          ...state,            // keep anything else (admin, etc.)
          golfer: updatedGolfer,
        },
      });
    };

  return (
    <div className="container mt-5">

    <section className="vip-step4 container my-5">
  <div className="row justify-content-center">
    <div className="col-lg-12">
    <h3 className="mb-3 text-uppercase">
  {pageTitle || "Purchase a VIP Card"}
</h3>
{(pageOverview !== undefined ? pageOverview : "The VIP Card offers golfers exclusive discounted rates at approximately 200 courses throughout Virginia and West Virginia. A portion of the proceeds go to support the VSGA-VIP Scholarship Foundation.") && (
  <p className="mb-4">
    {pageOverview || "The VIP Card offers golfers exclusive discounted rates at approximately 200 courses throughout Virginia and West Virginia. A portion of the proceeds go to support the VSGA-VIP Scholarship Foundation."}
  </p>
)}
      <div className="">
        <div className="card-body">

          <div
            className="bg-light border px-3 py-2 mb-4 text-uppercase fw-semibold"
            style={{ fontSize: "0.9rem" }}
          >
            Additional Options
          </div>

          <p className="mb-4">
          Bundle and save — add extras to enhance your purchase before completing your order.
          </p>

          {loading && (
        <div className="text-center my-4">
          <div className="spinner-border" role="status" />
          <p className="mt-2 text-muted">Loading VIP add-ons</p>
        </div>
      )}
    

          <form id="step4-form" className="row gy-4" onSubmit={handleSubmit}>
  
          {products.map((product, index) => (
            <div className="col-md-6 d-flex justify-content-center" key={index}>
            <div className="border rounded shadow-sm card vip-card">
           
              <div className="vip-card-img">
                <img
                  src={product.imageUrl}
                  alt="VSGA Bag Tag"
                  className="img img-fluid rounded-top"
                />
              </div>

       
              <div className="vip-card-body p-3">
                <h3 className="text-center mb-2">
                  VSGA BAG TAG
                </h3>

                <div className="col-12 form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="bagTag"
                    name="bagTag"
                    checked={!!formData.bagTag} 
                    onChange={handleFieldChange("bagTag")}
                  />
                  <label className="form-check-label" htmlFor="bagTag">
                  Yes, I would like to add a personalized wooden Signs by the Sea bag tag to my order for an additional ${product.price?.toFixed(2)} (Allow approximately 6-8 weeks for delivery.)
                  </label>
                </div>


            
                {formData.bagTag && (
                    <div className="mt-3">
                    
                        <label className="form-label mb-1" htmlFor="donationAmount">
                                Personalization
                              </label>
                              <input
                                type="text"
                                id="personalization"
                                name="personalization"
                                className="form-control"
                                placeholder="Please enter your personalization"
                                value={formData.personalization || ""}
                                onChange={handleFieldChange("personalization")}
                                required
                              />
                      </div>
                  )}
              </div>
            </div>
          </div>
          ))}      
        






            <div className="col-md-6 d-flex justify-content-center">
              <div className="border rounded shadow-sm card vip-card">
        
                <div className="vip-card-img">
                  <img
                    src={appAsset("assets/foundation.png")}
                    alt="Scholarship Donation"
                    className="img-fluid rounded-top"
                  />
                </div>

                <div className="vip-card-body p-3">
                  <h3 className="text-center mb-4 pb-2">
                    SCHOLARSHIP DONATION
                  </h3>

                  <div className="col-md-12 form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="donation"
                      name="donation"
                      checked={!!formData.donation} 
                      onChange={handleFieldChange("donation")}

                    />
                    <label className="form-check-label" htmlFor="donation">
                      Yes, I would like to make a donation to the VSGA-VIP Scholarship Foundation.
                    </label>
                  </div>


                  {formData.donation && (
        <div className="mt-3">
         
            <label className="form-label mb-1" htmlFor="donationAmount">
                    Donation Amount
                  </label>
                  <input
                    type="number"
                    id="donation_amount"
                    name="donation_amount"
                    className="form-control"
                    placeholder="Please enter your donation amount"
                    min="1"
                    step="1"
                    value={formData.donation_amount && formData.donation_amount > 0 ? formData.donation_amount : ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      updateField("donation_amount", v === "" ? 0 : Number(v));
                    }}
                    required
                  />
          </div>
      )}

                  
                </div>
              </div>
            </div>

         
            <div className="col-12 d-flex justify-content-between align-items-center mt-4">
              <button
                type="button"
                id="step4-back"
                className="btn btn-link text-decoration-none px-0"
                onClick={() =>
                  navigate("/step4", {
                    state: {
                      ...location.state,          // preserve all routing state
                      golfer: location.state?.golfer ?? null  // updated golfer stays
                    }
                  })
                }
              >
                ← Back
              </button>

              <button
                type="submit"
                className="btn btn-primary px-4"
              >
                Next
              </button>
            </div>

          </form>

        </div>
      </div>
    </div>
  </div>
  {/* FOOTER NOTE */}
  <Footer />
</section>







      
    </div>
  );
};

export default Step5;
